import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Link,
  Divider as MuiDivider,
  Button as MuiButton 
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../services/api';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { Logo } from '../../components/layout/Logo';
import { getTestId } from '../../utils/testUtils';

const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await auth.login(usernameOrEmail, password);
      login(response.data.token, {
        id: response.data.user.id,
        username: response.data.user.username,
        is_staff: response.data.user.is_admin,
        has_completed_intake: response.data.user.has_completed_intake
      });
      
      // Redirect to intake assessment if not completed
      if (!response.data.user.has_completed_intake) {
        navigate('/flows/intake_assessment_v0');
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError('Invalid username/email or password');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default', // This will use the dark theme background
        position: 'relative',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Logo style={{ height: '80px' }} />
          </Box>

          <Card
            sx={{
              width: '100%',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(38, 38, 38, 0.6)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                mb: 4,
                fontWeight: 700,
                color: 'text.primary'
              }}
            >
              Welcome to A Wise Friend
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }} {...getTestId('login-form')}>
              {error && (
                <Typography 
                  color="error" 
                  sx={{ 
                    mb: 2,
                    textAlign: 'center'
                  }}
                >
                  {error}
                </Typography>
              )}

              <Input
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username or Email"
                name="username"
                autoComplete="username email"
                autoFocus
                value={usernameOrEmail}
                onChange={(e) => setUsernameOrEmail(e.target.value)}
                sx={{ mb: 2 }}
                {...getTestId('login-username')}
              />

              <Input
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ 
                  mt: 2,
                  mb: 2,
                  height: '48px',
                  fontSize: '1rem'
                }}
                {...getTestId('login-submit')}
              >
                Sign In
              </Button>

              <MuiDivider sx={{ my: 2, color: 'text.secondary', fontSize: '0.875rem' }}>OR</MuiDivider>

              <MuiButton
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={() => {
                  const backendBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000';
                  window.location.href = `${backendBaseUrl}/accounts/google/login/?process=login`;
                }}
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                Sign in with Google
              </MuiButton>

              <Box sx={{ textAlign: 'center' }}>
                <Link 
                  href="/register/organization" 
                  variant="body2"
                  sx={{ 
                    cursor: 'pointer',
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {"Don't have an account? Sign Up"}
                </Link>
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
